import React from "react";
import {
  BundleLineVariantId,
  ImageAsset,
  Product,
  ProductId,
} from "@greeter/core";
import {
  Card,
  CardProps,
  OpenClosedIndicator,
  QuantityControls,
  useTimeout,
} from "@greeter/matter-v2";
import { classNames } from "@greeter/util";
import { IconLock } from "@tabler/icons-react";
import { Thumb } from "./Thumb";
import { AnimatePresence, motion } from "motion/react";
import {
  combine,
  dropInOutFromBottom,
  dropInOutFromTop,
  staggerForwardsBackwards,
} from "@greeter/motion";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { formatMoney } from "@greeter/formatters";
import { toast } from "react-toastify";
import { IonIcon, useIonAlert } from "@ionic/react";
import { alert, checkmark } from "ionicons/icons";
import {
  bundleLineLocked,
  bundleLineExtraQuantity,
  bundleLinePickedQuantity,
  bundleLineExtraPrice,
  bundleLineAdditionalPrice,
  bundleLineLimitReached,
} from "./util";
import { Log } from "@greeter/log";
import { useBundlePicker } from "./BundlePickerV3Provider";
import { BundleLineForm, BundleLineFormSchema } from "../../models";

import css from "./BundleLineVariantPicker.module.scss";
import { indexBy } from "@greeter/map";

type AccordionContextProps = {
  open?: string;
  toggle?: (id: string) => void;
};
const AccordionContext = React.createContext<AccordionContextProps>({});

export function AccordionProvider(props: React.PropsWithChildren) {
  const [open, setOpen] = useState<string | undefined>();
  return (
    <AccordionContext
      value={{
        open: open,
        toggle(id: string) {
          if (open === id) setOpen(undefined);
          else setOpen(id);
        },
      }}
      {...props}
    />
  );
}

function useAccordion() {
  return useContext(AccordionContext);
}

/**
 * Find neares ion-content and scroll to element
 */
function scrollTo(args: { el?: HTMLElement | null }) {
  requestAnimationFrame(() => {
    if (!args.el) return;
    const ionContent = args.el.closest("ion-content");
    if (ionContent) {
      ionContent.getScrollElement().then((scrollEl) => {
        if (!args.el) return;
        console.debug("[scrollTo]", { target: args.el.offsetTop, el: args.el });
        scrollEl.scrollTo({
          top: args.el.offsetTop - 20,
          behavior: "smooth",
        });
      });
    }
  });
}

export type OnChange = (change: { bundleLine: BundleLineForm }) => void;
export type BundleLineVariantPickerProps = CardProps & {
  bundleLine: BundleLineForm;
  thumbUrl?: string;
  onPickDifferentVariant?: (change: { variantId: BundleLineVariantId }) => void;
  onPickNewVariant?: () => void;
  onChange?: OnChange;

  indexedProducts?: Map<ProductId, Product>;

  active?: boolean;
};

export function BundleLineVariantPicker({
  className,
  onChange,
  active,
  bundleLine,
  ...props
}: BundleLineVariantPickerProps) {
  const { open: openId, toggle: toggleFn } = useAccordion();
  function toggle() {
    toggleFn?.(bundleLine.id);
  }
  const open = openId === bundleLine.id;

  const locked = bundleLineLocked(bundleLine);

  const ref = useRef<HTMLDivElement>(null);

  function pickedQuantity() {
    let r = 0;
    for (const variant of bundleLine.variants) {
      r += variant.quantity ?? 0;
    }
    return r;
  }

  useEffect(() => {
    if (open) {
      scrollTo({ el: ref.current });
    }
  }, [open]);

  const extraPrice =
    bundleLineExtraPrice(bundleLine) + bundleLineAdditionalPrice(bundleLine);

  const context = useBundlePicker();

  return (
    <motion.div
      ref={ref}
      layout
      style={{ position: "relative" }}
      data-testid={props["data-testid"]}
    >
      <Card
        layout
        style={{ borderRadius: 10 }}
        {...classNames(
          css.BundleLineVariantPicker,
          open && active && css.Active,
          // pickedQuantity() > bundleLine.quantity && "error-outline",
          className
        )}
        {...props}
      >
        <Card.Content
          layout
          onClick={() => {
            if (locked) {
              toast("Dette produkt er låst og kan ikke ændres");
              return;
            }
            toggle();
          }}
          style={{ top: 0, left: 0, cursor: "pointer" }}
        >
          <motion.div
            layout
            {...classNames(
              css.Header,
              open && css.Open,
              locked && css.Locked,
              props.thumbUrl && css.WithThumb
            )}
          >
            {props.thumbUrl && (
              <Thumb className={css.Thumb} src={props.thumbUrl} />
            )}
            <div className={css.Info}>
              <span className={css.Name}>
                {bundleLine.name}
                <AnimatePresence mode="popLayout">
                  {extraPrice > 0 && (
                    <motion.span
                      {...dropInOutFromBottom}
                      style={{
                        color: "var(--green-color-3)",
                        marginLeft: 5,
                      }}
                    >
                      (+{formatMoney(extraPrice)} kr.)
                    </motion.span>
                  )}
                </AnimatePresence>
              </span>
              <span className={css.Quantity}>{bundleLine.quantity} stk</span>
            </div>
            {!open && !locked && (
              <div style={{ display: "grid", placeItems: "center" }}>
                {/*<ProgressBar
                mode="strict"
                value={bundleLinePickedQuantity()}
                target={bundleLine.quantity}
              />*/}
              </div>
            )}
            {locked ? <IconLock /> : <OpenClosedIndicator open={open} />}
          </motion.div>
        </Card.Content>
        <AnimatePresence mode="popLayout">
          {open && (
            <Card.Content
              layout="position"
              key={`BundleLineVariantPicker__${bundleLine.id}__content`}
              className={css.QuantityPicker}
              {...dropInOutFromTop}
              style={{ padding: 10 }}
            >
              <FocusProvider>
                <div className={css.QuantityPickerContent}>
                  {bundleLine.variants
                    .concat(
                      bundleLine.extra
                        .filter(
                          (e) =>
                            !bundleLine.variants.find(
                              (v) => v.id === e.bundleLineVariantId!
                            )
                        )
                        .map(
                          (e) =>
                            bundleLine.variants.find(
                              (v) => v.id === e.bundleLineVariantId!
                            )!
                        )
                    )
                    .filter(
                      (v) =>
                        (v.quantity ?? 0) ||
                        (bundleLine.extra.find(
                          (e) => e.bundleLineVariantId === v.id
                        )?.quantity ?? 0) > 0
                    )
                    .map((v, i) => {
                      const product = props.indexedProducts?.get(v.productId);
                      let thumbUrl = "";
                      if (product) {
                        thumbUrl = ImageAsset.findUriWithSizeOrDefault(
                          product.coverAsset,
                          "1x1-w100"
                        );
                      }
                      return (
                        <InnerBundleLineVariantPicker
                          id={v.id}
                          data-testid={`inner-bundleline-variant-picker-${v.id}`}
                          key={`InnerBundleLineVariantPicker__${v.id}__${i}`}
                          additionalPrice={v.additionalPrice}
                          quantity={v.quantity ?? 0}
                          pickedQuantity={bundleLinePickedQuantity(bundleLine)}
                          extraQuantity={bundleLineExtraQuantity(
                            bundleLine,
                            v.id
                          )}
                          extraPrice={v.salesPrice}
                          maxQuantity={bundleLine.quantity}
                          thumbUrl={thumbUrl}
                          onChangeQuantity={(value, direction) => {
                            Log.debug(
                              `[BundleLineVariantPicker][onChangeQuantity]`,
                              {
                                value,
                                direction,
                              }
                            );
                            const copy = BundleLineFormSchema.parse(bundleLine);
                            copy.extra ??= [];
                            const indexVariants = indexBy(
                              copy.variants,
                              (v) => v.id
                            );

                            let limitReached = bundleLineLimitReached(copy);
                            const extra = copy.extra.find(
                              (e) => e.bundleLineVariantId === v.id
                            );
                            const variant = copy.variants.find(
                              (bv) => bv.id === v.id
                            );

                            const cheapestExtra = copy.extra
                              .toSorted(
                                (a, b) =>
                                  (indexVariants.get(a.bundleLineVariantId)
                                    ?.salesPrice ?? 0) -
                                  (indexVariants.get(b.bundleLineVariantId)
                                    ?.salesPrice ?? 0)
                              )
                              .at(0);
                            const cheapestExtraVariant = indexVariants.get(
                              cheapestExtra?.bundleLineVariantId ?? ""
                            );

                            const shouldMoveCheapestQuantity =
                              !extra &&
                              variant &&
                              limitReached &&
                              cheapestExtraVariant &&
                              cheapestExtra &&
                              direction === "decrement";

                            const shouldSetExtraQuantity =
                              extra && variant && limitReached;

                            const shouldSetVariantQuantity = !extra && variant;

                            const shouldAddExtra =
                              variant &&
                              limitReached &&
                              direction === "increment";

                            if (shouldMoveCheapestQuantity) {
                              const diff = value - (variant.quantity ?? 0);
                              variant.quantity = value;
                              cheapestExtra.quantity += diff;
                              cheapestExtraVariant.quantity ??= 0;
                              cheapestExtraVariant.quantity -= diff;
                            } else if (shouldSetExtraQuantity) {
                              variant.quantity ??= 0;
                              extra.quantity = value - variant.quantity;
                            } else if (shouldAddExtra) {
                              variant.quantity ??= 0;
                              copy.extra.push({
                                quantity: value - variant.quantity,
                                bundleLineVariantId: v.id,
                                bundleLineId: copy.id,
                              });
                            } else if (shouldSetVariantQuantity) {
                              variant.quantity ??= 0;
                              variant.quantity = Math.max(value, 0);
                            }
                            copy.extra = copy.extra.filter(
                              (e) => e.quantity > 0
                            );
                            onChange?.({ bundleLine: copy });
                          }}
                          onPickVariant={() =>
                            props.onPickDifferentVariant?.({ variantId: v.id })
                          }
                          onPickNewVariant={props.onPickNewVariant}
                          onRemoveVariant={() => {
                            let copy = BundleLineFormSchema.parse(bundleLine);
                            copy.variants = copy.variants.filter(
                              (cv) => cv.id !== v.id
                            );
                            onChange?.({ bundleLine: copy });
                          }}
                          name={v.name}
                          index={i}
                        />
                      );
                    })}
                  {(pickedQuantity() < bundleLine.quantity ||
                    context.featureFlags["upsell.v1.guest"]) && (
                    <Card
                      key="add-new-variant"
                      layout="position"
                      className={css.GreenButton}
                      onClick={props.onPickNewVariant}
                      {...dropInOutFromTop}
                    >
                      <Card.Content style={{ padding: "10px 0" }}>
                        + Tilføj ny drikkevare
                      </Card.Content>
                    </Card>
                  )}
                </div>
              </FocusProvider>
            </Card.Content>
          )}
        </AnimatePresence>
      </Card>
      <AnimatePresence mode="popLayout">
        <motion.div
          layout
          {...combine(dropInOutFromTop, {
            animate: {
              backgroundColor:
                bundleLinePickedQuantity(bundleLine) === bundleLine.quantity
                  ? "var(--gm-color-primary-highlight)"
                  : "var(--gm-color-yellow)",
            },
          })}
          {...classNames(css.IndicatorContainer, css.Warning)}
          style={{
            minHeight: 25,
            minWidth: 25,
            borderRadius: 10000,
          }}
          data-testid={`status-${bundleLine.id}`}
        >
          {bundleLinePickedQuantity(bundleLine) === bundleLine.quantity && (
            <motion.div
              layout="position"
              className={css.IndicatorIconContainer}
              data-testid="valid"
            >
              <IonIcon icon={checkmark} className={css.IndicatorIcon} />
            </motion.div>
          )}
          {bundleLinePickedQuantity(bundleLine) !== bundleLine.quantity && (
            <motion.div
              layout="position"
              className={css.IndicatorIconContainer}
            >
              <IonIcon
                icon={alert}
                className={css.IndicatorIcon}
                style={{ color: "var(--gm-color-default-bg)", width: 15 }}
              />
            </motion.div>
          )}
          {bundleLinePickedQuantity(bundleLine) !== bundleLine.quantity && (
            <motion.span
              layout="position"
              {...dropInOutFromBottom}
              style={{ paddingRight: 10 }}
              data-testid="invalid"
            >
              {bundleLinePickedQuantity(bundleLine)} ud af {bundleLine.quantity}
            </motion.span>
          )}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
}

type FocusContextProps = {
  focusedId?: string;
  focus: (id: string) => void;
  unfocus: () => void;
};
export const FocusContext = React.createContext<FocusContextProps>({
  focus() {
    throw new Error("Not implemented");
  },
  unfocus() {
    throw new Error("Not implemented");
  },
});

/**
 * Context to provide "we're focusing on this context"
 * with an auto-cancelling focus feature which listens to any click.
 * In order to avoid having onfocus on key buttons, remember to .stopPropagration otherwise
 * the auto-cancel happens every time you're trying to trigger a focus.
 */
export function FocusProvider(
  props: React.PropsWithChildren & {
    idleThresholdMs?: number;
    repeatThreshold?: number;
  }
) {
  const currentId = useRef<string | undefined>(undefined);
  const repeats = useRef<number>(0);
  const [focused, setFocused] = useState<string | undefined>();
  const timeoutId = useRef<ReturnType<typeof setTimeout>>(null);
  const idleThresholdMs = props.idleThresholdMs ?? 2_000;

  const focus = useCallback(function focus(id: string) {
    if (currentId.current !== id) {
      repeats.current = 0;
      currentId.current = id;
    }
    if (
      repeats.current < (props.repeatThreshold ?? 1) &&
      currentId.current === id
    ) {
      repeats.current++;
      return;
    }

    setFocused(id);
    if (timeoutId.current) clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => {
      setFocused(undefined);
      repeats.current = 0;
      currentId.current = undefined;
    }, idleThresholdMs);
  }, []);

  const unfocus = useCallback(function unfocus() {
    if (timeoutId.current) clearTimeout(timeoutId.current);
    setFocused(undefined);
    repeats.current = 0;
    currentId.current = undefined;
  }, []);

  /**
   * This is the auto-unfocus feature.
   */
  useEffect(() => {
    document.addEventListener("click", unfocus);
    return () => document.removeEventListener("click", unfocus);
  }, [unfocus]);

  return (
    <FocusContext value={{ focus, unfocus, focusedId: focused }} {...props} />
  );
}

function useFocusedContext() {
  return useContext(FocusContext);
}

export type InnerBundleLineVariantPickerProps = {
  id: string;
  name: string;

  quantity: number;
  maxQuantity: number;
  pickedQuantity: number;

  additionalPrice: number;

  /**
   * Used when we exceed the sales price, it's counted as "Extra"
   */
  extraQuantity: number;
  /**
   * The price for each extra addition
   */
  extraPrice: number;

  changedTo?: { id: string; name: string };

  onChangeQuantity?: (
    value: number,
    direction: "decrement" | "increment"
  ) => void;
  onRemoveVariant?: () => void;

  onPickNewVariant?: () => void;

  /**
   * Used when the user intends to swap out the current variant
   */
  onPickVariant?: () => void;
  onAnimationEnd?: () => void;
  onAnimationStart?: () => void;
  thumbUrl?: string;
  index: number;
  showIndex?: boolean;

  locked?: boolean;

  styles?: {
    content?: React.CSSProperties;
  };
  size?: "sm" | "md";
};
export function InnerBundleLineVariantPicker(
  props: InnerBundleLineVariantPickerProps
) {
  const focused = useFocusedContext();
  const context = useBundlePicker();

  // const isFocused = focused.focusedId && focused.focusedId === props.id;
  // const isFocusedButNotThis = focused.focusedId && focused.focusedId !== props.id;
  const isFocused = false;
  const isFocusedButNotThis = false;

  Log.debug(`[InnerBundleLineVariantPicker]`, {
    quantity: props.quantity,
    extraQuantity: props.extraQuantity,
  });

  const deletionGuardTimeout = useTimeout();
  const [deletionGuard, setDeletionGuard] = useState(false);

  const additionGuardTimeout = useTimeout();
  const [additionGuard, setAdditionGuard] = useState(false);
  const [additionExceeded, setAdditionExceeded] = useState(false);

  const [alert] = useIonAlert();

  function setupOnClick(
    action?: (ev: React.SyntheticEvent) => void
  ): (ev: React.SyntheticEvent) => void {
    return function onClick(ev) {
      if (isFocusedButNotThis) return;
      ev.stopPropagation();
      focused.focus(props.id);
      action?.(ev);
    };
  }

  const limitReached =
    props.pickedQuantity >= props.maxQuantity &&
    !context.featureFlags["upsell.v1.guest"];

  function incrementer(v: number) {
    return function () {
      if (limitReached) {
        Log.info("[InnerBundleLineVariantPicker][incrementer] limit reached");
        return;
      }
      const total = props.quantity + props.extraQuantity + v;

      if (
        props.maxQuantity === props.pickedQuantity + props.extraQuantity &&
        !additionGuard &&
        !additionExceeded
      ) {
        setAdditionGuard(true);

        additionGuardTimeout.setTimeout(() => {
          setAdditionGuard(false);
        }, 10_000);

        return;
      }

      if (additionGuard && !additionExceeded) {
        setAdditionGuard(false);
        setAdditionExceeded(true);
      }

      props.onChangeQuantity?.(total, "increment");
    };
  }

  function decrementer(v: number) {
    return function () {
      const total = props.quantity + props.extraQuantity + v;

      if (total <= props.pickedQuantity) {
        setAdditionExceeded(false);
        if (additionGuard) {
          setAdditionGuard(false);
          return;
        }
      }

      if (total <= 0) {
        setDeletionGuard(true);
        deletionGuardTimeout.setTimeout(() => {
          setDeletionGuard(false);
        }, 2_000);
      }

      if (deletionGuard && total <= 0) {
        alert({
          mode: "ios",
          message: "Fjern?",
          buttons: [
            { text: "Nej" },
            {
              text: "Ja",
              handler() {
                props.onChangeQuantity?.(total, "decrement");
              },
            },
          ],
        });
      }
      if (total > 0) {
        props.onChangeQuantity?.(total, "decrement");
      }
    };
  }

  return (
    <motion.div
      data-testid={props["data-testid"]}
      layout="position"
      {...staggerForwardsBackwards(
        combine(dropInOutFromTop, {
          animate: {
            opacity: isFocusedButNotThis ? 0.2 : 1,
          },
        }),
        0,
        props.index,
        props.quantity
      )}
      style={{
        borderRadius: 10,
        zIndex: isFocused ? 1000 : undefined,
        ...{ "--thumb-size": props.size === "md" ? "50px" : "30px" },
      }}
      onAnimationStart={props.onAnimationStart}
      onAnimationEnd={props.onAnimationEnd}
    >
      <Card
        {...classNames(
          css.InnerBundleLineVariantPicker,
          deletionGuard && css.Delete,
          isFocusedButNotThis && css.Disabled,
          props.changedTo && css.Changed,
          props.size === "md" && css.MediumSize
        )}
      >
        <Card.Content
          onClick={setupOnClick(props.onPickVariant)}
          style={{ padding: 10, ...props.styles?.content }}
        >
          <motion.div className={css.Content} style={{ gap: 10 }}>
            {props.thumbUrl && (
              <Thumb
                className={css.Thumb}
                src={props.thumbUrl}
                size={props.size ?? "sm"}
              />
            )}
            <motion.div layout className={css.Info}>
              <motion.span layout="position" className={css.Name}>
                <AnimatePresence mode="popLayout">
                  {!deletionGuard && !additionGuard && (
                    <motion.span key="name" {...dropInOutFromTop}>
                      {props.name}
                    </motion.span>
                  )}
                  {additionGuard && (
                    <motion.span key="extra" {...dropInOutFromBottom}>
                      Tilføj ekstra{" "}
                      <span
                        style={{
                          color: "var(--green-color-3)",
                        }}
                      >
                        +{formatMoney(props.extraPrice)} kr.
                      </span>
                      ?
                    </motion.span>
                  )}
                  {deletionGuard && (
                    <motion.span key="delete" {...dropInOutFromBottom}>
                      Slet?
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.span>
              {(!!(props.additionalPrice > 0 && props.quantity > 0) ||
                !!(props.extraPrice && props.extraQuantity > 0)) && (
                <motion.span
                  {...dropInOutFromBottom}
                  layout="position"
                  style={{
                    color: "var(--green-color-3)",
                    textAlign: "left",
                    fontSize: 12,
                  }}
                >
                  +
                  {formatMoney(
                    props.additionalPrice * props.quantity +
                      props.extraPrice * props.extraQuantity
                  )}{" "}
                  kr.
                </motion.span>
              )}
            </motion.div>
            <QuantityControls>
              <QuantityControls.EditButton
                disabled={additionGuard}
                data-testid="btn-edit"
              />
              {deletionGuard ? (
                <QuantityControls.TrashButton
                  onClick={setupOnClick(decrementer(-1))}
                  data-testid="btn-trash"
                />
              ) : additionGuard ? (
                <QuantityControls.CancelButton
                  onClick={setupOnClick(decrementer(-1))}
                  data-testid="btn-cancel"
                />
              ) : (
                <QuantityControls.MinusButton
                  onClick={setupOnClick(decrementer(-1))}
                  data-testid="btn-minus"
                />
              )}
              <QuantityControls.Value
                data-testid="input-quantity"
                value={props.quantity + (props.extraQuantity ?? 0)}
                onClick={(e) => e.stopPropagation()}
                onBlur={(e) => {
                  let r = Math.min(
                    Math.max(parseInt(e.target.value), 1),
                    props.maxQuantity
                  );
                  if (isNaN(r)) {
                    r = 1;
                  }
                  props.onChangeQuantity?.(
                    r,
                    r < props.quantity + (props.extraQuantity ?? 0)
                      ? "decrement"
                      : "increment"
                  );
                }}
                onChange={(e) => {
                  let r = parseInt(e.target.value);
                  if (isNaN(r)) {
                    r = 1;
                  }
                  props.onChangeQuantity?.(
                    r,
                    r < props.quantity + (props.extraQuantity ?? 0)
                      ? "decrement"
                      : "increment"
                  );
                }}
              />
              {additionGuard ? (
                <QuantityControls.ConfirmButton
                  data-testid="btn-confirm"
                  disabled={limitReached}
                  onClick={setupOnClick(incrementer(+1))}
                />
              ) : (
                <QuantityControls.PlusButton
                  data-testid="btn-plus"
                  disabled={limitReached}
                  onClick={setupOnClick(incrementer(+1))}
                />
              )}
            </QuantityControls>
          </motion.div>
        </Card.Content>
      </Card>
    </motion.div>
  );
}
